import { useCallback, useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../core/services/utilitiesService.ts'
import { buildReportingGridExcelPayload } from '../../../state_manager/utils/reportingGridExcelExport.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'

type ExportRow = Record<string, unknown>

/**
 * The ribbon Export of a reporting grid: every row under the current filters
 * and sort (all pages), in the grid's columns, through the shared
 * reporting-grid Excel payload. The file is `<title> - <YYYY-MM-DD>.xlsx`.
 *
 * `fetchAll` reads every page for the grid's current filters and sort (one of
 * the `fetchAll*ForExport` helpers) - memoise it on those. `keepRow` drops
 * synthesised rows, e.g. the per-currency Total rows, which Excel users total
 * themselves (as the Funds grid's export does).
 */
export function useReportingGridExport({
  title,
  gridColumns,
  fetchAll,
  keepRow
}: {
  title: string
  gridColumns: ViewConfigColumn[]
  fetchAll: (accessToken: string | null) => Promise<{ data: ExportRow[]; error?: string }>
  keepRow?: (row: ExportRow) => boolean
}) {
  const [exportLoading, setExportLoading] = useState(false)

  const handleExport = useCallback(async () => {
    setExportLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const { data, error } = await fetchAll(token)
      if (error) {
        showErrorToast(error)
        return
      }
      const rows = keepRow ? data.filter(keepRow) : data
      if (rows.length === 0) {
        toast.info('No rows to export')
        return
      }
      const payload = buildReportingGridExcelPayload(gridColumns, rows)
      const filename = `${title} - ${dayjs().format('YYYY-MM-DD')}.xlsx`
      const result = await exportJsonToExcel({ ...payload, filename }, token)
      if (result.success) {
        toast.success(`${filename} exported successfully`)
      } else {
        showErrorToast(result.error || 'Export failed')
      }
    } finally {
      setExportLoading(false)
    }
  }, [title, gridColumns, fetchAll, keepRow])

  return { exportLoading, handleExport }
}
